import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Home, ScanLine, Award, Wallet, User, LogOut } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { SimpleSelect } from "@/components/common";

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const TABS = [
  { to: "/portal", label: "Início", icon: Home, end: true },
  { to: "/portal/checkin", label: "Check-in", icon: ScanLine },
  { to: "/portal/graduacao", label: "Graduação", icon: Award },
  { to: "/portal/financeiro", label: "Financeiro", icon: Wallet },
  { to: "/portal/perfil", label: "Perfil", icon: User },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState(localStorage.getItem("amt_sid") || "");

  useEffect(() => {
    api.get("/portal/me").then(({ data }) => {
      setStudents(data.students);
      if (!data.students.find((s) => s.id === sid)) setSid(data.students[0]?.id || "");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (sid) localStorage.setItem("amt_sid", sid); }, [sid]);

  const out = async () => { await logout(); nav("/login"); };

  return (
    <PortalCtx.Provider value={{ sid, students, student: students.find((s) => s.id === sid) }}>
      <div className="min-h-screen pb-24 md:pb-8 bg-slate-50">
        <header className="mat-texture text-white">
          <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <LogoMark className="w-10 h-10" />
              <div className="leading-none">
                <div className="font-display font-extrabold tracking-wide">AUGUSTO MACIEL TEAM</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold">{user.role === "responsavel" ? "Área do responsável" : "Área do aluno"}</div>
              </div>
            </div>
            <button onClick={out} className="text-zinc-400 hover:text-white" data-testid="portal-logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
          <nav className="hidden md:flex max-w-3xl mx-auto px-4 gap-1 pb-2">
            {TABS.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} data-testid={`portal-tab-${t.label.toLowerCase()}`}
                className={({ isActive }) => `px-3 py-1.5 rounded-md text-sm font-semibold ${isActive ? "bg-red-600" : "text-zinc-400 hover:text-white"}`}>
                {t.label}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="max-w-3xl mx-auto px-4 py-5">
          {students.length > 1 && (
            <div className="mb-4" data-testid="student-switcher">
              <SimpleSelect value={sid} onChange={setSid} testid="student-switcher-select"
                options={students.map((s) => ({ value: s.id, label: s.full_name }))} />
            </div>
          )}
          {students.length === 0 ? (
            <div className="panel p-6 text-sm text-slate-600" data-testid="no-student-linked">Nenhum aluno vinculado ao seu acesso. Procure a administração.</div>
          ) : sid ? <Outlet /> : null}
        </main>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-zinc-950 border-t border-white/10 grid grid-cols-5">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`portal-bottom-${t.label.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold uppercase tracking-wide ${isActive ? "text-red-500" : "text-zinc-500"}`}>
              <t.icon className="w-5 h-5" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </PortalCtx.Provider>
  );
}
